// Common
import { Component, inject, Injector, signal } from '@angular/core';

// Services
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { RecurrenceEditModalService } from '@modules/calendar-app/services/recurrence-edit-modal.service';

// Types
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { VirtualFolder } from '@modules/calendar-app/types/virtual-folder';
import { Application } from '@modules/common/types/application';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';
import { DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import type { CalendarSummaryDto } from '@sharedlib/dtos/dto/calendar-app/calendar.dto';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { catchError, finalize, map, of, switchMap, take, takeUntil, tap, timeout } from 'rxjs';
import { AccountService } from '@frontendlib/common/services/account.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { MainViewService } from '@modules/common/services/main-view.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { type SidebarItemComponent } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { type Space } from '@modules/settings/types/space';
import { Team } from '@modules/settings/types/team';
import { CalendarTenantScopeService } from '../../services/calendar-tenant-scope.service';
import { workspaceColor, workspacePalette } from '@modules/common/utils/workspace-color';
import { calendarPinnedMenu } from '../sidebar-context-menu/calendar-rail-menu';

// Components
import { BaseSidebarComponent } from '@modules/common/components/base-sidebar/base-sidebar.component';

@Component({
  selector: 'app-events-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.less'],
  standalone: false,
})
export class EventsSideBarComponent extends BaseSidebarComponent<Calendar, CalendarEvent, VirtualFolder> {
  application = Application.calendar;
  sidebarSplitViewKey = SidebarSplitViewKey.calendar;
  selfDragDataTypes = [DragDataTypes.event, DragDataTypes.calendar];

  // Make enum accessible in template
  public readonly SidebarSplitViewKey = SidebarSplitViewKey;

  public baseItems: SidebarItem[] = [
    { title: 'Events', id: 'all_events', icon: 'sapphire-events' },
    // Filter folders over the mailbox, not event containers — see SBUX-911.
    { title: 'Received Invites', id: 'received_invites', icon: 'sapphire-inbox' },
    { title: 'Sent Invites', id: 'sent_invites', icon: 'sapphire-sent' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
    { title: 'Follow Up', id: 'followed', icon: 'sapphire-follow-up' },
    { title: 'Snoozed', id: 'snoozed', icon: 'sapphire-snooze' },
    { title: 'Archive', id: 'archived', icon: 'sapphire-archive' },
    { title: 'Trash', id: 'deleted', icon: 'sapphire-trash' },
  ];

  public readonly primaryItems = ['all_events', 'flagged', 'received_invites', 'sent_invites'].flatMap((id) =>
    this.baseItems.filter((item) => item.id === id),
  );
  public readonly moreItems = this.baseItems.filter((item) => !this.primaryItems.includes(item));
  /** My Workspace's primary rows: the prototype names the member's first row "My Events" (P/app.js:99). */
  public readonly myPrimaryItems: SidebarItem[] = [
    { ...this.primaryItems[0], title: 'My Events' },
    ...this.primaryItems.slice(1),
  ];
  /** The Pinned / Team Knots and Tags rows' menus: the prototype's entry, then Calendar's own Add and Manage. */
  protected readonly pinnedMenus = {
    mine: { knots: calendarPinnedMenu('knots', 'mine'), tags: calendarPinnedMenu('tags', 'mine') },
    team: { knots: calendarPinnedMenu('knots', 'team'), tags: calendarPinnedMenu('tags', 'team') },
  } as const;
  protected readonly workspaceResolved = signal(false);
  protected readonly myWorkspaceExpanded = signal(true);
  protected readonly teamWorkspaceExpanded = signal(true);
  protected readonly currentTeamWorkspaceExpanded = signal(true);
  protected readonly moreExpanded = signal(false);
  protected readonly teamMoreExpanded = signal(false);
  protected readonly workspaceColor = workspaceColor;
  protected readonly workspacePalette = workspacePalette;
  protected currentTeamWorkspace: Space | undefined;
  protected currentMemberTenant: Team | undefined;
  protected currentTeamTenant: Team | undefined;
  protected currentTeamWorkspaceInitials = '';
  private readonly context = inject(SpaceContextStore);
  private readonly accountService = inject(AccountService);
  private readonly spacesService = inject(SpacesService);
  private readonly calendarScope = inject(CalendarTenantScopeService);
  private dropTenant: string | undefined;

  public override ngOnInit(): void {
    super.ngOnInit();
    this.context.contextId$
      .pipe(
        tap(() => this.workspaceResolved.set(false)),
        switchMap((contextId) =>
          this.context.type() === SpaceType.PERSONAL
            ? of({ items: [] as Space[], contextId })
            : this.accountService.getAccount(true).pipe(
                switchMap((account) =>
                  this.spacesService.listWithInvites({ archived: false, limit: 100 }, { account }),
                ),
                take(1),
                // Bound the whole account→spaces lookup before the scope's ten-second last-resort gate.
                timeout({ first: 8_000 }),
                map(({ items }) => ({ items, contextId })),
                catchError(() => of(null)),
              ),
        ),
        takeUntil(this.alive),
      )
      .subscribe((result) => {
        if (!result) {
          this.currentTeamWorkspace = undefined;
          this.currentMemberTenant = undefined;
          this.currentTeamTenant = undefined;
          this.calendarScope.degradeToPersonal();
        } else {
          this.currentTeamWorkspace = result.items.find(
            ({ currentUserStatus, id }) => currentUserStatus === 'accepted' && id === result.contextId,
          );
          this.currentMemberTenant = this.currentTeamWorkspace?.myMailboxTenantId
            ? new Team({ id: this.currentTeamWorkspace.myMailboxTenantId })
            : undefined;
          this.currentTeamTenant = this.currentTeamWorkspace
            ? new Team({ id: this.currentTeamWorkspace.id })
            : undefined;
          if (this.currentTeamWorkspace && !this.currentMemberTenant) {
            this.calendarScope.degradeToPersonal();
            this.currentTeamWorkspace = undefined;
            this.currentTeamTenant = undefined;
          } else {
            this.calendarScope.resolveInitialTenant(this.currentMemberTenant?.id ?? null);
          }
          this.currentTeamWorkspaceInitials =
            this.currentTeamWorkspace?.title
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((word) => word[0])
              .join('')
              .toUpperCase() ?? '';
        }
        this.workspaceResolved.set(true);
      });
  }

  protected dndDropForTenant(data: DragData, folder: VirtualFolder, row: SidebarItemComponent, tenant?: Team): void {
    this.dropTenant = tenant?.id;
    if (data.type !== DragDataTypes.event && data.type !== DragDataTypes.calendar) {
      this.calendarScope.selectTenant(tenant?.id ?? null);
    }
    try {
      this.dndDrop(data, folder, this.elements.toArray().indexOf(row));
    } finally {
      this.dropTenant = undefined;
    }
  }

  private readonly recurrenceEditModalService = inject(RecurrenceEditModalService);
  private readonly formChangesModalsService = inject(FormChangesModalsService);
  private readonly mainViewService = inject(MainViewService);
  private closingForEvents = false;

  constructor(
    injector: Injector,
    private calendarsService: CalendarsService,
    private eventsService: EventsService,
  ) {
    super(injector);
  }

  /**
   * Actions
   */

  /**
   * The Events grid only draws while nothing is open in the third panel, so choosing Events (a switch or a
   * re-click, under any tenant) also closes what is open, as its close button would (SBUX-1080). An edited
   * item asks the same "Unsaved changes" question first; Stay keeps both the item and the current folder, and
   * a repeat click while that question is open is ignored. Other folders keep the open item.
   */
  override selectFilter(filter: VirtualFolder, team: Team | undefined = this.team): void {
    if (filter !== 'all_events') {
      super.selectFilter(filter, team);
      return;
    }
    if (this.closingForEvents) return;
    const open = this.mainViewService.getCurrentMainView();
    this.closingForEvents = true;
    (open ? this.formChangesModalsService.confirmUnsavedChanges(open.id) : of(true))
      .pipe(
        take(1),
        takeUntil(this.alive),
        finalize(() => (this.closingForEvents = false)),
      )
      .subscribe((canLeave) => {
        if (!canLeave) return;
        super.selectFilter(filter, team);
        this.stateService.setMainView(null);
      });
  }

  handleMove(dragData: DragData, sidebarFolder: VirtualFolder) {
    if (sidebarFolder === 'following') return;
    const ids = this.getIds(dragData.data);

    // Archive and Trash are lifecycle transitions, not field writes: events take the dedicated
    // endpoints, and a single recurring event is asked for its scope exactly as the context menu asks
    // (a multi-select stays silent and applies "this event" per occurrence).
    if (dragData.type === DragDataTypes.event && (sidebarFolder === 'archived' || sidebarFolder === 'deleted')) {
      const events = dragData.data as CalendarEventSummaryDto[];
      const toArchive = sidebarFolder === 'archived';
      this.recurrenceEditModalService
        .askForItems(events, toArchive ? 'Archive recurring event?' : 'Delete recurring event?')
        .pipe(
          take(1),
          switchMap((mode) =>
            toArchive
              ? this.eventsService.archive({ ids }, true, mode, { tenantItems: events })
              : this.eventsService.delete({ ids }, true, mode, { tenantItems: events }),
          ),
        )
        .subscribe();
      return;
    }

    if (sidebarFolder === 'deleted') {
      if (dragData.type === DragDataTypes.calendar) {
        this.calendarsService.delete({ ids }, true, undefined, { tenantItems: dragData.data as CalendarSummaryDto[] });
      }
      return;
    }

    // Restore trashed/archived items first — virtual-folder lists filter out archived/deleted
    // items, so applying the changes alone would leave them stuck in Trash/Archive.
    const restoreOptions = { skipArchiveRestore: sidebarFolder === 'archived' };

    if (dragData.type === DragDataTypes.event) {
      const { changes, message } = CalendarEvent.getChangesForVirtualFolder(sidebarFolder);
      moveItemsIntoContainer(
        this.eventsService,
        dragData.data,
        () =>
          this.eventsService.bunchUpdate({ ids }, changes, {
            message,
            tenantItems: dragData.data as CalendarEventSummaryDto[],
          }),
        restoreOptions,
      );
    } else if (dragData.type === DragDataTypes.calendar) {
      const { changes, message } = Calendar.getChangesForVirtualFolder(sidebarFolder);
      moveItemsIntoContainer(
        this.calendarsService,
        dragData.data,
        () =>
          this.calendarsService.bunchUpdate({ ids }, changes, {
            message,
            tenantItems: dragData.data as CalendarSummaryDto[],
          }),
        restoreOptions,
      );
    }
  }

  handleMore(item: CalendarEvent) {
    this.stateService.setMainView(item);
  }

  getStitchFromDragData(dragData: DragData, sidebarFolder: VirtualFolder): CalendarEvent {
    const { changes } = CalendarEvent.getChangesForVirtualFolder(sidebarFolder);

    return new CalendarEvent({ ...changes, accountId: this.dropTenant, title: dragData.data[0]['title'] });
  }
}
